/**
 * CharityDashboard.tsx
 * 
 * Display charity donation statistics for ZiaGivesBack
 * Shows total donations, recent contributions, and monthly breakdown
 */

import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  LinearProgress,
  Avatar,
} from '@mui/material';

// Icons
import CloseIcon from '@mui/icons-material/Close';
import VolunteerActivismIcon from '@mui/icons-material/VolunteerActivism';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PeopleIcon from '@mui/icons-material/People';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import LeaderboardIcon from '@mui/icons-material/Leaderboard';

interface LeaderboardEntry {
  rank: number;
  username: string;
  mealsProvided: number;
  totalDonated: number;
  isCurrentUser?: boolean;
}

interface CharityStats {
  totals: {
    donations: number;
    tonAmount: string;
    usdEstimate: string;
    monthlyContributions: string;
    yearlyContributions: string;
  };
  recentDonations: {
    id: number;
    amount: string;
    type: string;
    percentage: number;
    date: string;
    txHash: string;
  }[];
  monthlyBreakdown: {
    month: string;
    donation_type: string;
    count: number;
    total_ton: number;
  }[];
  streak: number;
  charityInfo: {
    name: string;
    wallet: string;
    description: string;
    percentages: Record<string, number>;
  };
  leaderboard?: LeaderboardEntry[];
  userRank?: number;
  userMeals?: number;
}

interface CharityDashboardProps {
  open: boolean;
  onClose: () => void;
}

export function CharityDashboard({ open, onClose }: CharityDashboardProps) {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState<CharityStats | null>(null);
  const [error, setError] = useState<string | null>(null);
  
  const loadStats = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch('/api/charity');
      if (!response.ok) throw new Error('Failed to load charity stats');
      const data = await response.json();
      setStats(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => {
    if (open) {
      loadStats();
    }
  }, [open]);
  
  // Format date
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: 'numeric'
    });
  };
  
  // Get donation type label
  const getDonationTypeLabel = (type: string) => {
    if (type.includes('monthly')) return 'Monthly Sub';
    if (type.includes('yearly')) return 'Annual Sub';
    if (type.includes('business_plus')) return 'Business+';
    return 'Direct';
  };
  
  // Get donation type color
  const getDonationTypeColor = (type: string) => {
    if (type.includes('monthly')) return '#60a5fa';
    if (type.includes('yearly')) return '#22c55e';
    if (type.includes('business_plus')) return '#f59e0b';
    return '#a78bfa';
  };
  
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#0f172a',
          backgroundImage: 'linear-gradient(135deg, rgba(34, 197, 94, 0.05) 0%, rgba(96, 165, 250, 0.05) 100%)',
          minHeight: '70vh'
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'space-between',
        borderBottom: '1px solid rgba(34, 197, 94, 0.3)',
        background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.1) 0%, rgba(96, 165, 250, 0.1) 100%)'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <VolunteerActivismIcon sx={{ color: '#22c55e' }} />
          <Typography variant="h6">ZiaGivesBack</Typography>
          <Chip 
            label="Community Charity" 
            size="small" 
            sx={{ 
              backgroundColor: 'rgba(34, 197, 94, 0.2)',
              color: '#22c55e',
              fontWeight: 600
            }} 
          />
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={loadStats} sx={{ color: '#94a3b8' }}>
            <RefreshIcon />
          </IconButton>
          <IconButton onClick={onClose} sx={{ color: '#94a3b8' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 3 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: '#22c55e' }} />
          </Box>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography sx={{ color: '#ef4444', mb: 2 }}>{error}</Typography>
            <Button variant="outlined" onClick={loadStats}>Retry</Button>
          </Box>
        ) : stats ? (
          <>
            {/* Description Banner */}
            <Box sx={{ 
              p: 2, 
              mb: 3, 
              borderRadius: 2,
              backgroundColor: 'rgba(34, 197, 94, 0.1)',
              border: '1px solid rgba(34, 197, 94, 0.2)'
            }}>
              <Typography sx={{ color: '#94a3b8', fontSize: '0.9rem', lineHeight: 1.6 }}>
                💚 {stats.charityInfo.description}. A portion of every subscription 
                ({stats.charityInfo.percentages.monthly}% monthly, {stats.charityInfo.percentages.yearly}% annual) 
                goes directly to community initiatives.
              </Typography>
            </Box>
            
            {/* Stats Cards */}
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 2, mb: 4 }}>
              {/* Total Donated */}
              <Card sx={{ 
                backgroundColor: 'rgba(34, 197, 94, 0.1)',
                border: '1px solid rgba(34, 197, 94, 0.3)'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <AccountBalanceWalletIcon sx={{ fontSize: 40, color: '#22c55e', mb: 1 }} />
                  <Typography variant="h4" sx={{ color: '#22c55e', fontWeight: 700 }}>
                    {stats.totals.tonAmount}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    TON Donated
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    ≈ ${stats.totals.usdEstimate} USD
                  </Typography>
                </CardContent>
              </Card>
              
              {/* Total Donations Count */}
              <Card sx={{ 
                backgroundColor: 'rgba(96, 165, 250, 0.1)',
                border: '1px solid rgba(96, 165, 250, 0.3)'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <PeopleIcon sx={{ fontSize: 40, color: '#60a5fa', mb: 1 }} />
                  <Typography variant="h4" sx={{ color: '#60a5fa', fontWeight: 700 }}>
                    {stats.totals.donations}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Total Donations
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    From Premium Subscribers
                  </Typography>
                </CardContent>
              </Card>
              
              {/* Streak */}
              <Card sx={{ 
                backgroundColor: 'rgba(245, 158, 11, 0.1)',
                border: '1px solid rgba(245, 158, 11, 0.3)'
              }}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <LocalFireDepartmentIcon sx={{ fontSize: 40, color: '#f59e0b', mb: 1 }} />
                  <Typography variant="h4" sx={{ color: '#f59e0b', fontWeight: 700 }}>
                    {stats.streak}
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#94a3b8' }}>
                    Month Streak
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#64748b' }}>
                    Consecutive Months Active
                  </Typography>
                </CardContent>
              </Card>
            </Box>
            
            {/* Contribution Breakdown */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <TrendingUpIcon sx={{ color: '#60a5fa' }} />
                Contribution Sources
              </Typography>
              
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#60a5fa' }}>
                      Monthly Subs (10%)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#60a5fa' }}>
                      {stats.totals.monthlyContributions} TON
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={
                      parseFloat(stats.totals.tonAmount) > 0
                        ? (parseFloat(stats.totals.monthlyContributions) / parseFloat(stats.totals.tonAmount)) * 100
                        : 0
                    }
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(96, 165, 250, 0.2)',
                      '& .MuiLinearProgress-bar': { backgroundColor: '#60a5fa' }
                    }}
                  />
                </Box>
              </Box>
              
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography variant="body2" sx={{ color: '#22c55e' }}>
                      Annual Subs (20%)
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#22c55e' }}>
                      {stats.totals.yearlyContributions} TON
                    </Typography>
                  </Box>
                  <LinearProgress 
                    variant="determinate" 
                    value={
                      parseFloat(stats.totals.tonAmount) > 0
                        ? (parseFloat(stats.totals.yearlyContributions) / parseFloat(stats.totals.tonAmount)) * 100
                        : 0
                    }
                    sx={{ 
                      height: 8, 
                      borderRadius: 4,
                      backgroundColor: 'rgba(34, 197, 94, 0.2)',
                      '& .MuiLinearProgress-bar': { backgroundColor: '#22c55e' }
                    }}
                  />
                </Box>
              </Box>
            </Box>
            
            {/* Leaderboard Section */}
            <Box sx={{ mb: 4 }}>
              <Typography variant="subtitle1" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <LeaderboardIcon sx={{ color: '#f59e0b' }} />
                Top Contributors This Month
              </Typography>
              
              {stats.leaderboard && stats.leaderboard.length > 0 ? (
                <Box sx={{ 
                  backgroundColor: 'rgba(30, 41, 59, 0.5)', 
                  borderRadius: 2,
                  border: '1px solid rgba(245, 158, 11, 0.2)',
                  overflow: 'hidden'
                }}>
                  {stats.leaderboard.slice(0, 10).map((entry, idx) => {
                    const isTop3 = entry.rank <= 3;
                    const rankEmoji = entry.rank === 1 ? '🥇' : entry.rank === 2 ? '🥈' : entry.rank === 3 ? '🥉' : null;
                    const bgColor = entry.isCurrentUser 
                      ? 'rgba(34, 197, 94, 0.15)' 
                      : isTop3 
                        ? 'rgba(245, 158, 11, 0.08)' 
                        : 'transparent';
                    
                    return (
                      <Box
                        key={entry.rank}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          p: 1.5,
                          borderBottom: idx < Math.min(stats.leaderboard!.length, 10) - 1 
                            ? '1px solid rgba(96, 165, 250, 0.1)' 
                            : 'none',
                          backgroundColor: bgColor,
                          transition: 'background-color 0.2s'
                        }}
                      >
                        {/* Rank */}
                        <Box sx={{ 
                          width: 40, 
                          textAlign: 'center',
                          fontWeight: 700,
                          fontSize: isTop3 ? '1.2rem' : '0.9rem',
                          color: isTop3 ? '#f59e0b' : '#64748b'
                        }}>
                          {rankEmoji || `#${entry.rank}`}
                        </Box>
                        
                        {/* User info */}
                        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Avatar 
                            sx={{ 
                              width: 32, 
                              height: 32, 
                              backgroundColor: entry.isCurrentUser ? '#22c55e' : '#3b82f6',
                              fontSize: '0.9rem'
                            }}
                          >
                            {entry.username.charAt(0).toUpperCase()}
                          </Avatar>
                          <Box>
                            <Typography sx={{ 
                              color: entry.isCurrentUser ? '#22c55e' : 'white', 
                              fontWeight: entry.isCurrentUser ? 700 : 500,
                              fontSize: '0.9rem'
                            }}>
                              @{entry.username}
                              {entry.isCurrentUser && (
                                <Chip 
                                  label="You" 
                                  size="small" 
                                  sx={{ 
                                    ml: 1, 
                                    height: 18,
                                    fontSize: '0.65rem',
                                    backgroundColor: 'rgba(34, 197, 94, 0.3)',
                                    color: '#22c55e'
                                  }} 
                                />
                              )}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {/* Meals provided */}
                        <Box sx={{ textAlign: 'right' }}>
                          <Typography sx={{ 
                            color: '#f59e0b', 
                            fontWeight: 700,
                            fontSize: '0.95rem'
                          }}>
                            🍽️ {entry.mealsProvided}
                          </Typography>
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            meals
                          </Typography>
                        </Box>
                      </Box>
                    );
                  })}
                  
                  {/* Show user's rank if not in top 10 */}
                  {stats.userRank && stats.userRank > 10 && (
                    <Box sx={{ 
                      p: 2, 
                      borderTop: '1px dashed rgba(96, 165, 250, 0.3)',
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                        Your rank: <strong style={{ color: '#22c55e' }}>#{stats.userRank}</strong>
                      </Typography>
                      <Typography sx={{ color: '#f59e0b', fontWeight: 600, fontSize: '0.85rem' }}>
                        🍽️ {stats.userMeals || 0} meals
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box sx={{ 
                  textAlign: 'center', 
                  py: 3, 
                  backgroundColor: 'rgba(30, 41, 59, 0.5)', 
                  borderRadius: 2,
                  border: '1px solid rgba(245, 158, 11, 0.1)'
                }}>
                  <EmojiEventsIcon sx={{ fontSize: 40, color: '#64748b', mb: 1 }} />
                  <Typography sx={{ color: '#64748b' }}>
                    Be the first on the leaderboard!
                  </Typography>
                  <Typography variant="caption" sx={{ color: '#475569' }}>
                    Subscribe to start contributing
                  </Typography>
                </Box>
              )}
            </Box>
            
            {/* Recent Donations */}
            <Box>
              <Typography variant="subtitle1" sx={{ color: 'white', mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <CalendarMonthIcon sx={{ color: '#a78bfa' }} />
                Recent Contributions
              </Typography>
              
              {stats.recentDonations.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 3, backgroundColor: 'rgba(30, 41, 59, 0.5)', borderRadius: 2 }}>
                  <Typography sx={{ color: '#64748b' }}>
                    No donations yet. Be the first to contribute!
                  </Typography>
                </Box>
              ) : (
                <List sx={{ 
                  backgroundColor: 'rgba(30, 41, 59, 0.5)', 
                  borderRadius: 2,
                  border: '1px solid rgba(96, 165, 250, 0.1)'
                }}>
                  {stats.recentDonations.map((donation, idx) => (
                    <ListItem 
                      key={donation.id}
                      sx={{ 
                        borderBottom: idx < stats.recentDonations.length - 1 
                          ? '1px solid rgba(96, 165, 250, 0.1)' 
                          : 'none'
                      }}
                    >
                      <ListItemIcon>
                        <Avatar sx={{ 
                          backgroundColor: getDonationTypeColor(donation.type),
                          width: 36,
                          height: 36
                        }}>
                          <VolunteerActivismIcon sx={{ fontSize: 18 }} />
                        </Avatar>
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography sx={{ color: 'white', fontWeight: 600 }}>
                              {donation.amount} TON
                            </Typography>
                            <Chip
                              label={getDonationTypeLabel(donation.type)}
                              size="small"
                              sx={{
                                backgroundColor: `${getDonationTypeColor(donation.type)}20`,
                                color: getDonationTypeColor(donation.type),
                                fontSize: '0.7rem'
                              }}
                            />
                            <Chip
                              label={`${donation.percentage}%`}
                              size="small"
                              sx={{
                                backgroundColor: 'rgba(34, 197, 94, 0.2)',
                                color: '#22c55e',
                                fontSize: '0.7rem'
                              }}
                            />
                          </Box>
                        }
                        secondary={
                          <Typography variant="caption" sx={{ color: '#64748b' }}>
                            {formatDate(donation.date)}
                          </Typography>
                        }
                      />
                      {donation.txHash && !donation.txHash.startsWith('stripe_') && (
                        <IconButton
                          size="small"
                          component="a"
                          href={`https://tonviewer.com/transaction/${donation.txHash}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ color: '#60a5fa' }}
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      )}
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
            
            {/* Wallet Info */}
            <Box sx={{ 
              mt: 4, 
              p: 2, 
              borderRadius: 2,
              backgroundColor: 'rgba(30, 41, 59, 0.8)',
              border: '1px solid rgba(96, 165, 250, 0.2)'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" sx={{ color: '#64748b', mb: 0.5 }}>
                    Charity Wallet (TON Blockchain)
                  </Typography>
                  <Typography sx={{ color: '#0098ea', fontFamily: 'monospace' }}>
                    {stats.charityInfo.wallet}
                  </Typography>
                </Box>
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<OpenInNewIcon />}
                  component="a"
                  href={`https://tonviewer.com/${stats.charityInfo.wallet}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ 
                    borderColor: '#0098ea',
                    color: '#0098ea'
                  }}
                >
                  View on TON
                </Button>
              </Box>
            </Box>
            
            {/* Impact Statement */}
            <Box sx={{ 
              mt: 3, 
              p: 3, 
              borderRadius: 2,
              background: 'linear-gradient(135deg, rgba(34, 197, 94, 0.15) 0%, rgba(96, 165, 250, 0.15) 100%)',
              textAlign: 'center'
            }}>
              <EmojiEventsIcon sx={{ fontSize: 48, color: '#f59e0b', mb: 1 }} />
              <Typography variant="h6" sx={{ color: 'white', mb: 1 }}>
                Your Subscription Makes a Difference
              </Typography>
              <Typography sx={{ color: '#94a3b8', maxWidth: 500, mx: 'auto' }}>
                Every premium subscription automatically contributes to community initiatives 
                across the Permian Basin. Together, we're building a stronger oilfield community.
              </Typography>
            </Box>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

export default CharityDashboard;
